import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL, authFetch } from '../../utils/auth';
import ToolSidebar from '../../utils/ToolSideBar';
import '../../../css/partner/PartnerClients.css';

const FILTERS = [
  { value: '', label: 'All Clients' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'approved_sales', label: 'Approved / Sales' },
  { value: 'active_project', label: 'Active Project' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed / Lost' },
];

const APPROVED_REVIEW_STATUSES = ['approved'];
const NON_ACTIVE_REVIEW_STATUSES = ['declined', 'rejected', 'withdrawn'];
const NON_ACTIVE_REQUEST_STATUSES = ['declined', 'archived', 'withdrawn', 'rejected'];
const TERMINAL_DELIVERY_STATUSES = ['launched', 'completed', 'canceled', 'cancelled', 'rejected'];

function isApprovedProject(project) {
  return project.status === 'accepted' || APPROVED_REVIEW_STATUSES.includes(project.review_status || '');
}

function isNonActiveRequest(project) {
  return NON_ACTIVE_REQUEST_STATUSES.includes(project.status || '') || NON_ACTIVE_REVIEW_STATUSES.includes(project.review_status || '');
}

function isClosedTerminalDelivery(project) {
  return ['canceled', 'cancelled', 'rejected'].includes(project.delivery_status || '');
}

function isTerminalDelivery(project) {
  return TERMINAL_DELIVERY_STATUSES.includes(project.delivery_status || '');
}

function projectActivity(project = {}) {
  const approved = isApprovedProject(project);
  const converted = Boolean(project.converted_project);
  const terminal = isTerminalDelivery(project);

  if (isNonActiveRequest(project) || (approved && converted && terminal && isClosedTerminalDelivery(project))) {
    return { key: 'closed', label: 'Closed', projectStatus: 'Closed' };
  }
  if (approved && converted && terminal) {
    return { key: 'completed', label: project.delivery_status_label || 'Completed', projectStatus: project.delivery_status_label || 'Completed' };
  }
  if (approved && converted) {
    return { key: 'active_project', label: 'Active', projectStatus: 'Active Project' };
  }
  if (approved) {
    return { key: 'approved_sales', label: 'Approved', projectStatus: 'Approved' };
  }
  return { key: 'pending_approval', label: 'Pending Approval', projectStatus: project.review_status_label || project.status_label || 'Pending Approval' };
}

function normalizeClient(client) {
  const project = client.latest_project || {};
  const activity = projectActivity(project);
  const deliveryLabel = project.delivery_status_label || (activity.key === 'active_project' ? 'Active Project' : 'Not started');

  return {
    ...client,
    client_name: client.client_name || client.name || client.email || 'Client',
    business_name: client.business_name || project.business_name || project.business || 'Project Request',
    statusKey: activity.key,
    statusLabel: activity.label,
    approvalStatus: isApprovedProject(project) ? 'Approved' : project.review_status_label || project.status_label || 'Pending Approval',
    projectStatus: activity.projectStatus,
    currentStage: activity.key === 'pending_approval' ? project.review_status_label || project.status_label || 'Pending Approval' : deliveryLabel,
    nextAction: activity.key === 'pending_approval' ? 'Awaiting SCD approval' : 'No action needed',
    preferredContact: project.preferred_contact_method || 'Email',
    projectCount: client.project_count || 0,
    currentProject: project.business_name || project.business || 'Project Request',
  };
}

function MetricCard({ label, value }) {
  return <article className='partner-client-metric'><span>{label}</span><strong>{value}</strong></article>;
}

function ClientInfo({ label, value }) {
  return <div className='partner-client-info'><span>{label}</span><strong>{value || 'Not provided'}</strong></div>;
}

function ClientCard({ client }) {
  return (
    <article className='partner-client-card'>
      <div className='partner-client-card-header'>
        <div><h2>{client.client_name}</h2><p>{client.business_name}</p></div>
        <span className={'partner-client-status ' + client.statusKey}>{client.statusLabel}</span>
      </div>
      <div className='partner-client-info-grid'>
        <ClientInfo label='Current Stage' value={client.currentStage} />
        <ClientInfo label='Projects' value={client.projectCount + ' ' + (client.projectCount === 1 ? 'Project' : 'Projects')} />
        <ClientInfo label='Next Action' value={client.nextAction} />
        <ClientInfo label='Preferred Contact' value={client.preferredContact} />
      </div>
      <div className='partner-client-card-footer'>
        <span>{client.currentProject}</span>
        <Link className='partner-client-button' to={'/clients/' + client.id}>View Client</Link>
      </div>
    </article>
  );
}

export default function PartnerClients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadClients() {
      try {
        const response = await authFetch(API_URL + '/partner/clients/');
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Failed to load clients');
        const rawClients = Array.isArray(data) ? data : data.results || [];
        setClients(rawClients.map(normalizeClient));
      } catch (err) {
        console.error(err);
        setError(err.message || 'Failed to load clients');
      } finally {
        setLoading(false);
      }
    }
    loadClients();
  }, []);

  const filteredClients = useMemo(() => {
    const term = search.trim().toLowerCase();
    return clients.filter((client) => {
      const matchesSearch = !term || [client.client_name, client.business_name, client.email].some((value) => (value || '').toLowerCase().includes(term));
      const matchesFilter = !filter || client.statusKey === filter;
      return matchesSearch && matchesFilter;
    });
  }, [clients, search, filter]);

  const metrics = useMemo(() => ({
    total: clients.length,
    pending: clients.filter((client) => client.statusKey === 'pending_approval').length,
    active: clients.filter((client) => client.statusKey === 'active_project').length,
    approved: clients.filter((client) => ['approved_sales', 'active_project', 'completed'].includes(client.statusKey)).length,
  }), [clients]);

  return (
    <div className='partner-client-shell'>
      <ToolSidebar active='clients' />
      <main className='partner-client-page'>
        <header className='partner-client-hero'><span>MY CLIENTS</span><h1>Clients</h1><p>Manage your client relationships, follow-ups, and active projects.</p></header>
        <section className='partner-client-metrics' aria-label='Partner client metrics'>
          <MetricCard label='Total Clients' value={metrics.total} />
          <MetricCard label='Pending Approval' value={metrics.pending} />
          <MetricCard label='Active Projects' value={metrics.active} />
          <MetricCard label='Approved Clients' value={metrics.approved} />
        </section>
        <section className='partner-client-toolbar' aria-label='Search and filter clients'>
          <input aria-label='Search clients' placeholder='Search clients, businesses, or email...' value={search} onChange={(event) => setSearch(event.target.value)} />
          <select aria-label='Filter clients' value={filter} onChange={(event) => setFilter(event.target.value)}>{FILTERS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
        </section>
        {loading ? <section className='partner-client-empty'><h2>Loading clients...</h2></section> : error ? <section className='partner-client-empty error'><h2>Unable to load clients</h2><p>{error}</p></section> : filteredClients.length === 0 ? <section className='partner-client-empty'><h2>No clients found</h2><p>Try adjusting your search or filters.</p></section> : <section className='partner-client-grid' aria-label='Partner clients'>{filteredClients.map((client) => <ClientCard key={client.id} client={client} />)}</section>}
      </main>
    </div>
  );
}
